import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './modules/App';
import * as serviceWorker from './serviceWorker';
import * as Sentry from '@sentry/react'
import { patchReactDomConflicts } from './utils/domPatch';
import { Amplify } from 'aws-amplify';
import { Auth } from './utils/amplifyWrapper';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux'
import store from "./store"
import { addMixPanelEvent } from './store/actions'
import { ERROR_OCCURRED } from './constants/mixpanel-events';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PreGenAudioPlayer } from './components/PreGenAudioPlayer';
// import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Only send analytics from real production hosts. Without this, the prod

const ANALYTICS_ALLOWED_HOSTNAMES = [
  'www.getlisten2it.com',
  'app.getlisten2it.com',
  'dashboard.getlisten2it.com',
  'pages.getlisten2it.com',
];
const isAnalyticsAllowedHost = ANALYTICS_ALLOWED_HOSTNAMES.includes(window.location.hostname);

patchReactDomConflicts();

// Bugsink (Sentry-compatible) error tracking — errors only, no performance/tracing
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_STAGE || process.env.NODE_ENV,
  tracesSampleRate: 0,
  // Drop client-connectivity failures (user lost internet). These are not app
  // bugs and would otherwise flood Bugsink with unactionable noise.
  ignoreErrors: [
    'Network Error',                                  // axios offline
    'Failed to fetch',                                // Chrome fetch offline
    'NetworkError when attempting to fetch resource', // Firefox fetch offline
    'Load failed',                                    // Safari fetch offline
    "Failed to execute 'removeChild' on 'Node'",      // FullStory/Hotjar DOM conflict (PD-946) — domPatch.js prevents most, this is a backstop for what it doesn't cover
    "Failed to execute 'insertBefore' on 'Node'",     // FullStory/Hotjar DOM conflict (PD-946) — domPatch.js prevents most, this is a backstop for what it doesn't cover
    'Invalid regular expression: invalid group specifier name', // Crisp chat widget bug on iOS 16 Safari/WebKit — 100% inside client.crisp.chat's own bundle, not our code
    'swbrowser.inNightMode is not a function',        // injected by Samsung Internet's in-app browser, not our code
    'isAllowAutoFillOnDomain',                        // Android WebView autofill injection ("Java object is gone"), not our code
  ],
  beforeSend(event, hint) {
    const err = hint && hint.originalException;
    // Browser is offline at send time, or axios flagged a transport failure.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;
    if (err && (err.code === 'ERR_NETWORK' || err.code === 'ERR_CANCELED')) return null;
    return event;
  },
})

const ErrorBoundary = Sentry.ErrorBoundary


const amplifyConfig = {
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID,
      loginWith: {
        oauth: {
          domain: process.env.REACT_APP_COGNITO_DOMAIN,
          scopes: ['email', 'openid', 'aws.cognito.signin.user.admin'],
          redirectSignIn: [process.env.REACT_APP_REDIRECT_SIGN_IN],
          redirectSignOut: [process.env.REACT_APP_REDIRECT_SIGN_OUT],
          responseType: 'code'
        },
        email: true,
        username: true
      }
    }
  },
  API: {
    REST: {
      main: {
        endpoint: process.env.REACT_APP_BASE_URL,
        region: process.env.REACT_APP_REGION
      },
      secondary: {
        endpoint: process.env.REACT_APP_BASE_URL,
        region: process.env.REACT_APP_REGION
      }
    },
    // Default auth mode for all REST APIs
    defaultAuthMode: 'userPool'
  }
};

Amplify.configure(amplifyConfig);

const tagManagerArgs = {
  gtmId: process.env.REACT_APP_GTM_ID,
};

// Mirrors the backend's isConfigured() gate — an empty GTM id on a non-prod
// build (e.g. staging) means analytics is intentionally unconfigured there,
// on top of the host allow-list check.
if (isAnalyticsAllowedHost && process.env.REACT_APP_GTM_ID) {
  TagManager.initialize(tagManagerArgs);
}

export const queryClient = new QueryClient()

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <ErrorBoundary
      onError={() => {
        // a crashed render is always a user-facing failure worth tracking
        store.dispatch(addMixPanelEvent({
          body: {
            eventName: ERROR_OCCURRED,
            eventData: {
              error_type: 'unhandled_exception',
              feature_name: 'app',
              page: window.location.pathname,
            },
          },
        }));
      }}
    >
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <App />
          <PreGenAudioPlayer />
        </Provider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
